<template>
  <div class="lib__form" data-cy="e-mobility-form">
    <header class="lib__form__header">
      <!-- Module TITRE -->
      <main-title>{{ $t('e-mobility.rechargeAtHome') }}</main-title>
      <!-- Fin Module TITRE -->

      <!-- Module BreadCrumb -->
      <breadcrumb :currentStep="currentStep" :lastStep="lastStep" :lastStepSeen="lastStepSeen" :stepDefinitions="stepDefinitions" @goToStep="goToStep" @reset="resetForm" :resetLabel="resetLabel" :isLastStepActive="false"></breadcrumb>
      <!-- Fin Module BreadCrumb -->
    </header>

    <api-errors />

    <!-- Module Step1 : Car Specification -->
    <CarSpecificationStep @canContinue="canContinue = $event" @previous="previousStep" @next="nextStep" ref="step1" v-if="currentStep === 1"></CarSpecificationStep>
    <!-- Fin Module Step1 : Car Specification -->

    <CarSelectedStep @canContinue="canContinue = $event" @previous="previousStep" @next="nextStep" ref="step2" v-if="currentStep === 2"></CarSelectedStep>

    <!-- Module Step2 :IdentificationStep -->
    <IdentificationStep @canContinue="canContinue = $event" @previous="previousStep" @next="nextStep" ref="step3" v-if="currentStep === 3"></IdentificationStep>
    <!-- Fin Module Step2 : IdentificationStep -->

    <!-- Module Step3 : Ean Result -->
    <GridAccessPointInfoOrInstallationStep @canContinue="canContinue = $event" @previous="previousStep" @next="checkNextStep" ref="step4" v-if="currentStep === 4"></GridAccessPointInfoOrInstallationStep>
    <!-- Fin Module Step3 : Ean Result -->

    <!-- Module Step4 : Consuming Device -->
    <ConsumingDeviceStep @canContinue="canContinue = $event" @previous="previousStep" @next="nextStep" ref="step5" v-if="currentStep === 5"></ConsumingDeviceStep>
    <!-- Fin Module Step4 : Consuming Device -->

    <!-- Module Step5 : ResultStep -->
    <ResultStep @canContinue="canContinue = $event" @previous="previousStep" @next="nextStep" ref="step6" v-if="currentStep === 6"></ResultStep>
    <!-- Fin Module Step5 : ResultStep -->
    <StepActions
      faqLabel="e-mobility.help.part1"
      :showFaq="checkShowFaq"
      :showChatButton="true"
      :faqLink="$t('e-mobility.help.helpLink')"
      :linkLabel="$t('e-mobility.help.part2')"
      :can-continue="canContinue"
      :has-next="currentStep < 6"
      :has-previous="currentStep > 1"
      :has-print="false"
      :has-reset="currentStep === 6"
      :loading="loading"
      @next="tryNextStep"
      @reset="resetForm"
      @previous="tryPreviousStep" />
  </div>

</template>

<script lang="ts">
import { Component } from 'vue-property-decorator'
import StepActions from '@/components/shared/StepActions.vue'
import Step from '@/components/steps/Step'
import { Form } from './Form'
import { StepDefinition } from '@ores/vue-library'
import { removeInterceptors } from '@/plugins/http-interceptors-global'
import { addEmobilityResponseInterceptors } from '@/plugins/http-response-interceptors-e-mobility'
import store from '@/store/index'
import CarSpecificationStep from '@/components/steps/e-mobility/CarSpecificationStep.vue'
import CarSelectedStep from '@/components/steps/e-mobility/CarSelectedStep.vue'
import DialogBox from '@/components/shared/DialogBox.vue'
import ApiErrors from '@/components/display/ApiErrors.vue'
import IdentificationStep from '@/components/steps/e-mobility/IdentificationStep.vue'
import ConsumingDeviceStep from '@/components/steps/e-mobility/ConsumingDeviceStep.vue'
import { Getter, Mutation, State } from 'vuex-class'
import GridAccessPointInfoOrInstallationStep from '@/components/steps/e-mobility/GridAccessPointInfoOrInstallationStep.vue'
import { ElectricityInstallation, ElectricMobilityGridAccessPointInfo } from '@/models'
import ResultStep from '@/components/steps/e-mobility/ResultStep.vue'

@Component({
  components: {
    StepActions,
    CarSpecificationStep,
    CarSelectedStep,
    ConsumingDeviceStep,
    IdentificationStep,
    GridAccessPointInfoOrInstallationStep,
    ResultStep,
    DialogBox,
    ApiErrors
  },
  name: 'e-mobility'
})
export default class EMobility extends Form {
  //#region [Data]
  @State('electricityInstallation', { namespace: 'eMobility/electricityInstallation' })
  public stateElectricityInstallation!: ElectricityInstallation

  @State('gridAccessPointInfo', { namespace: 'eMobility/identification' })
  public stateGridAccessPointInfo!: ElectricMobilityGridAccessPointInfo | null

  public stepDefinitions: StepDefinition[] = [
    new StepDefinition('car', 1, [new StepDefinition('', 2)]),
    new StepDefinition('electricity-installation', 3, [new StepDefinition('', 4)]),
    new StepDefinition('home-device', 5),
    new StepDefinition('result', 6)
  ]
  //#endregion

  //#region [Computed]
  public get checkShowFaq() {
    return this.currentStep === 4
  }

  public get resetLabel() {
    return this.currentStep === 6 ? this.$t('common.resetRequest') : null
  }
  //#endregion

  //#region [Getter]
  @Getter('getHasEan', { namespace: 'eMobility/identification' })
  public getHasEan!: string
  //#endregion

  //#region [Mutation]
  @Mutation('STORE_HAS_EAN', { namespace: 'eMobility/identification' })
  public storeHasEan!: (index: { hasEan: string | null }) => void
  //#endregion

  //#region [Method]
  public beforeCreate() {
    document.title = this.$t('e-mobility.formTitle').toString()
  }
  public mounted() {
    this.currentStep = 1
    this.lastStepSeen = 1
    this.canNavigateAfterLastStep = false

    this.onLangChanged(this.lang)

    if (this.interceptorId !== null) {
      removeInterceptors(this.$api, this.interceptorId)
    }
    const interceptorId = addEmobilityResponseInterceptors(this.$api, store)
    this.setInterceptorId(interceptorId)

    // Check maintenance
    this.$api.get('api/maintenanceManagement/electric-mobility')
  }

  public checkNextStep() {
    let isOk = false
    if (
      this.stateElectricityInstallation.meterType &&
      this.stateElectricityInstallation.electricityPowerSupplyType &&
      this.stateElectricityInstallation.power
    ) {
      isOk = true
    }
    if ((this.stateGridAccessPointInfo || isOk) && (!this.getHasEan || this.stateGridAccessPointInfo)) {
      this.nextStep()
    } else {
      this.storeHasEan({ hasEan: 'no' })
    }
  }

  public getStepComponent(): Step | null {
    return (this.$refs[`step${this.currentStep}`] as Step) || null
  }

  public resetForm() {
    this.currentStep = 1
    this.lastStepSeen = 1
    this.$store.dispatch('eMobility/reset')
    this.$nextTick(() => (this.currentStep = 1))
  }
  //#endregion
}
</script>
